import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertCircle,
  ArrowRight,
  Ban,
  Binary,
  CloudOff,
  Columns3,
  Crosshair,
  ExternalLink,
  FileDown,
  FileText,
  Fingerprint,
  GitCompareArrows,
  RotateCcw,
  Rows3,
  ShieldAlert,
  ShieldCheck,
  Timer,
  Type,
  UploadCloud,
} from "lucide-react";
import CompareBars from "../components/CompareBars";
import Counter from "../components/Counter";
import Dropzone from "../components/Dropzone";
import FingerprintStrip from "../components/FingerprintStrip";
import Gauge from "../components/Gauge";
import HistogramProjection from "../components/HistogramProjection";
import Magnetic from "../components/Magnetic";
import PipelineConsole from "../components/PipelineConsole";
import OfflineModal from "../components/OfflineModal";
import TrySample from "../components/TrySample";
import { Reveal } from "../components/Reveal";
import { compareStream, findSample, frozenCompare, readError, reportUrl, sampleUrl } from "../lib/api";
import { useIsOffline } from "../lib/health";
import { bytes, count, deltaDirection, deltaLabel, pctLabel, seconds } from "../lib/format";
import { DUR, EASE_OUT } from "../lib/motion";
import usePipeline from "../hooks/usePipeline";

/** Stage ids match the progress events emitted by backend/pipeline.py. */
const STAGES = [
  { id: "upload", name: "Upload documents", note: "client" },
  { id: "render", name: "Draw the page", note: "200 dpi" },
  { id: "project", name: "Count the pixels", note: "rows · columns" },
  { id: "extract", name: "Check every letter", note: "against the page" },
  { id: "fingerprint", name: "Chunk and hash", note: "k=5 · t=8" },
  { id: "score", name: "Score the similarity", note: "both readings" },
  { id: "report", name: "Build the PDF report", note: "fpdf2" },
];

const EXPECTED = [
  { icon: Binary, label: "Three similarity scores, worked out from the visible text" },
  { icon: GitCompareArrows, label: "The score a normal fingerprinting tool would have given" },
  { icon: ShieldAlert, label: "Every hidden letter, and where in the document it was" },
  { icon: Rows3, label: "The pixel counts used to read each page" },
  { icon: FileDown, label: "A PDF report covering the whole run" },
];

function InjectionFindings({ doc, label }) {
  const reasons = Object.entries(doc.reasons || {});
  return (
    <div className="finding" data-injected={doc.is_injected}>
      <div className="row row--between row--wrap">
        <span className="finding__name">
          {doc.is_injected ? (
            <ShieldAlert size={14} aria-hidden="true" style={{ color: "var(--rose-500)" }} />
          ) : (
            <ShieldCheck size={14} aria-hidden="true" style={{ color: "var(--accent)" }} />
          )}
          {label}
        </span>
        <span
          className={`badge ${
            !doc.is_injected
              ? "badge--accent"
              : doc.severity === "high"
              ? "badge--danger"
              : "badge--warn"
          }`}
        >
          {doc.is_injected ? `${doc.severity} severity` : "clean"}
        </span>
      </div>

      {doc.is_injected ? (
        <>
          <p className="tile__body">
            {count(doc.hidden_count)} of the {count(doc.raw_char_count)} letters in this file never
            show up on the page, leaving {count(doc.visible_char_count)} that do.
          </p>
          {doc.injected_chars?.length > 0 && (
            <div className="chip-set">
              {doc.injected_chars.slice(0, 8).map((char) => (
                <span className="chip" key={char}>
                  ‘{char}’
                </span>
              ))}
            </div>
          )}
          {reasons.length > 0 && (
            <div className="chip-set">
              {reasons.map(([reason, total]) => (
                <span className="chip" key={reason}>
                  {reason.replace(/-/g, " ")} · {total}
                </span>
              ))}
            </div>
          )}
          <p className="meta" style={{ fontSize: "var(--fs-3xs)" }}>
            The two readings agree on {pctLabel(doc.self_agreement, 2)} of the fingerprints
          </p>
        </>
      ) : (
        <p className="tile__body">
          All {count(doc.visible_char_count)} letters in this file show up on the page. Nothing is
          hidden.
        </p>
      )}
    </div>
  );
}

function Compare() {
  const prefersReducedMotion = useReducedMotion();
  const [fileOne, setFileOne] = useState(null);
  const [fileTwo, setFileTwo] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  //: True while the loaded files are the bundled samples. Gates the frozen
  //: result, so a document the user supplied is never given the sample's numbers.
  const [usingSample, setUsingSample] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const offline = useIsOffline();
  const controller = useRef(null);
  const pipeline = usePipeline(STAGES);
  const { running, start, handleEvent, succeed, fail, append, reset } = pipeline;

  useEffect(() => () => controller.current?.abort(), []);

  const submit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!fileOne || !fileTwo || running) return;

      setError(null);
      setResult(null);
      setFrozen(false);

      if (offline) {
        if (!usingSample) {
          setError({
            message:
              "The analysis service is offline, so your own documents cannot be processed. The bundled sample still works.",
            canRetry: false,
          });
          return;
        }
        start("service offline, loading the captured sample result");
        try {
          const data = await frozenCompare();
          setFrozen(true);
          succeed("Captured from a real run of this same pair");
          setResult(data);
        } catch (err) {
          const parsed = readError(err);
          setError(parsed);
          fail(parsed.message);
        }
        return;
      }

      controller.current = new AbortController();
      start(`POST /compare/stream  ${fileOne.name} + ${fileTwo.name}`);

      try {
        const data = await compareStream({
          fileOne,
          fileTwo,
          signal: controller.current.signal,
          onEvent: handleEvent,
        });
        append(
          `hidden characters: ${data.document_one.hidden_count} + ${data.document_two.hidden_count}`
        );
        succeed(`Done in ${seconds(data.elapsed_seconds)} · report ${data.report_id}`);
        setResult(data);
      } catch (err) {
        const parsed = readError(err);
        setError(parsed);
        fail(parsed.message);
      }
    },
    [fileOne, fileTwo, running, offline, usingSample, start, handleEvent, append, succeed, fail]
  );

  const cancel = useCallback(() => controller.current?.abort(), []);

  /** Drop the bundled original and its padded copy into the two dropzones. */
  const loadSample = useCallback(([original, copy]) => {
    setResult(null);
    setFrozen(false);
    reset();
    setUsingSample(true);
    setFileOne(original);
    setFileTwo(copy);
    setOfflineOpen(false);
  }, [reset]);

  const selectOne = useCallback((picked) => {
    setUsingSample(false);
    setFileOne(picked);
  }, []);

  const selectTwo = useCallback((picked) => {
    setUsingSample(false);
    setFileTwo(picked);
  }, []);

  const clearAll = useCallback(() => {
    controller.current?.abort();
    setFileOne(null);
    setFileTwo(null);
    setResult(null);
    setError(null);
    setUsingSample(false);
    setFrozen(false);
    reset();
  }, [reset]);

  const ready = Boolean(fileOne && fileTwo);
  const impactFlat = result ? deltaDirection(result.injection_impact) === "flat" : false;
  const transition = prefersReducedMotion ? { duration: 0 } : { duration: DUR.enter, ease: EASE_OUT };

  return (
    <div className="shell shell--wide work">
      <div className="work__head">
        <div>
          <span className="eyebrow">Two documents</span>
          <h1 className="work__title" style={{ marginTop: "var(--space-3)" }}>
            Document similarity
          </h1>
          <p className="work__lede">
            Upload an original and a suspected copy. If the copy has invisible letters padding it
            out, an ordinary plagiarism checker will report almost no match between them. DocCompare
            gives you both numbers: the score you would have got, and the real similarity with the
            padding ignored.
          </p>
        </div>
      </div>

      <div className="work__grid">
        {/* ---------------- INPUT RAIL ---------------- */}
        <form className="work__rail" onSubmit={submit} noValidate>
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">
                <UploadCloud size={13} aria-hidden="true" />
                Input
              </span>
              {(fileOne || fileTwo) && (
                <button type="button" className="btn btn--quiet btn--sm" onClick={clearAll}>
                  <RotateCcw size={12} aria-hidden="true" />
                  Reset
                </button>
              )}
            </div>
            <div className="panel__body work__drops">
              {!fileOne && !fileTwo && (
                <>
                  <TrySample
                    need={2}
                    onLoaded={loadSample}
                    onError={setError}
                    disabled={running}
                  />
                  <span className="trysample-divider">or upload your own</span>
                </>
              )}
              <Dropzone
                label="Document 1"
                accept=".pdf,.txt"
                hint="PDF or TXT. Letters can only be hidden in a PDF."
                file={fileOne}
                onSelect={selectOne}
                disabled={running}
                required
                blocked={offline}
                onBlockedClick={() => setOfflineOpen(true)}
                downloadUrl={usingSample && fileOne ? sampleUrl(fileOne.name) : undefined}
              />
              <Dropzone
                label="Document 2"
                accept=".pdf,.txt"
                hint="The document you are comparing it against."
                file={fileTwo}
                onSelect={selectTwo}
                disabled={running}
                required
                blocked={offline}
                onBlockedClick={() => setOfflineOpen(true)}
                downloadUrl={usingSample && fileTwo ? sampleUrl(fileTwo.name) : undefined}
              />
            </div>
            <div className="panel__foot runbar">
              <Magnetic strength={0.16}>
                <button
                  type="submit"
                  className="btn btn--primary btn--block btn--lg"
                  disabled={!ready || running}
                >
                  <span className="btn__sheen" aria-hidden="true" />
                  {running ? (
                    <>
                      <span className="stage__spin" aria-hidden="true" />
                      Analysing… {seconds(pipeline.elapsed)}
                    </>
                  ) : (
                    <>
                      <GitCompareArrows size={16} aria-hidden="true" />
                      Run comparison
                    </>
                  )}
                </button>
              </Magnetic>

              {running && (
                <button type="button" className="btn btn--danger btn--block btn--sm" onClick={cancel}>
                  <Ban size={12} aria-hidden="true" />
                  Cancel run
                </button>
              )}

              <p className="runbar__meta">
                <span>k = 5 · t = 8 · 200 dpi</span>
                <span>{ready ? `${bytes(fileOne.size + fileTwo.size)} queued` : "2 files required"}</span>
              </p>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                className="alert alert--danger"
                role="alert"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={transition}
              >
                <AlertCircle size={16} className="alert__icon" aria-hidden="true" />
                <div className="alert__body stack stack-2">
                  <span className="alert__title">Comparison failed</span>
                  <p>{error.message}</p>
                  {error.canRetry && (
                    <button type="submit" className="btn btn--ghost btn--sm" disabled={!ready}>
                      <RotateCcw size={12} aria-hidden="true" />
                      Try again
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>

        {/* ---------------- CONSOLE + RESULTS ---------------- */}
        <div className="work__main">
          <div className="panel">
            <div className="panel__head">
              <span className="panel__title">
                <Crosshair size={13} aria-hidden="true" />
                Pipeline
              </span>
              <span className="badge">
                {pipeline.status === "idle"
                  ? "Waiting"
                  : pipeline.status === "done"
                  ? "Complete"
                  : pipeline.status === "failed"
                  ? "Failed"
                  : seconds(pipeline.elapsed)}
              </span>
            </div>
            <div className="panel__body">
              <PipelineConsole
                stages={STAGES}
                stageStates={pipeline.stageStates}
                stageDetails={pipeline.stageDetails}
                log={pipeline.log}
                elapsed={pipeline.elapsed}
                status={pipeline.status}
              />
            </div>
          </div>

          {!result && !running && (
            <Reveal className="panel">
              <div className="panel__head">
                <span className="panel__title">
                  <FileText size={13} aria-hidden="true" />
                  What this run produces
                </span>
              </div>
              <div className="panel__body stack stack-4">
                <ul className="stack stack-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {EXPECTED.map(({ icon: Icon, label }) => (
                    <li className="row row--top" key={label}>
                      <Icon
                        size={15}
                        aria-hidden="true"
                        style={{ color: "var(--accent)", flexShrink: 0, marginTop: 3 }}
                      />
                      <span className="tile__body">{label}</span>
                    </li>
                  ))}
                </ul>

                <div className="alert alert--info">
                  <Timer size={16} className="alert__icon" aria-hidden="true" />
                  <div className="alert__body">
                    <p>
                      Every page has to be drawn before it can be read, so this is slower than a
                      plain text comparison. It works out to about 0.07 seconds a page.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {/* ---------------- RESULTS ---------------- */}
          <AnimatePresence>
            {result && (
              <motion.div
                className="results"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={prefersReducedMotion ? { duration: 0 } : { duration: DUR.reveal, ease: EASE_OUT }}
              >
                {frozen && (
                  <div className="alert alert--warn">
                    <CloudOff size={16} className="alert__icon" aria-hidden="true" />
                    <div className="alert__body">
                      <span className="alert__title">Captured result</span>
                      <p>
                        The service is offline, so these are the real numbers from an earlier run of
                        this same sample pair rather than a live analysis. Start the service to run
                        your own documents.
                      </p>
                    </div>
                  </div>
                )}

                <div className="impact" data-tone={impactFlat ? "neutral" : "alert"}>
                  <span className="impact__figure">
                    <Counter
                      value={Math.abs(result.injection_impact)}
                      mode="percent"
                      digits={1}
                      suffix=" pp"
                    />
                  </span>
                  <div className="impact__copy stack stack-2">
                    <span className="impact__title">
                      {impactFlat ? "Nothing hidden in either file" : "Difference the hidden text was making"}
                    </span>
                    {impactFlat ? (
                      <p className="tile__body">
                        Neither file has anything hidden in it, so both scores come out the same.{" "}
                        {pctLabel(result.similarity_after.jaccard, 1)} is their real similarity.
                      </p>
                    ) : (
                      <p className="tile__body">
                        An ordinary checker would report{" "}
                        {pctLabel(result.similarity_before.jaccard, 1)}. With the hidden letters taken
                        out, these two really score {pctLabel(result.similarity_after.jaccard, 1)}.
                        The hidden letters were worth {deltaLabel(result.injection_impact)}.
                      </p>
                    )}
                  </div>
                </div>

                <div className="results__split">
                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <ShieldCheck size={13} aria-hidden="true" />
                        Visible-text similarity
                      </span>
                      <span className="badge badge--accent">After cleaning</span>
                    </div>
                    <div className="panel__body">
                      <div className="gauge-grid">
                        <Gauge value={result.similarity_after.jaccard} label="Jaccard" delay={0} />
                        <Gauge value={result.similarity_after.dice} label="Dice" delay={0.1} />
                        <Gauge value={result.similarity_after.cosine} label="Cosine" delay={0.2} />
                      </div>
                    </div>
                  </div>

                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <FileText size={13} aria-hidden="true" />
                        Documents
                      </span>
                      <span className="badge">
                        <Timer size={11} aria-hidden="true" />
                        {seconds(result.elapsed_seconds)}
                      </span>
                    </div>
                    <div className="panel__body stack stack-4">
                      <div className="docpair">
                        <div className="docpair__side">
                          <div className="docpair__name">{result.document_one.name}</div>
                          <div className="docpair__meta">
                            {bytes(result.document_one.size_bytes)} ·{" "}
                            {result.document_one.page_count}p ·{" "}
                            {count(result.document_one.visible_char_count)} chars
                          </div>
                        </div>
                        <span className="docpair__link" aria-hidden="true">
                          <ArrowRight size={16} />
                        </span>
                        <div className="docpair__side">
                          <div className="docpair__name">{result.document_two.name}</div>
                          <div className="docpair__meta">
                            {bytes(result.document_two.size_bytes)} ·{" "}
                            {result.document_two.page_count}p ·{" "}
                            {count(result.document_two.visible_char_count)} chars
                          </div>
                        </div>
                      </div>

                      <dl className="kv" style={{ margin: 0 }}>
                        <dt className="kv__k">K-grams</dt>
                        <dd className="kv__v" style={{ margin: 0 }}>
                          {count(result.document_one.kgram_count)} /{" "}
                          {count(result.document_two.kgram_count)}
                        </dd>
                        <dt className="kv__k">Fingerprints</dt>
                        <dd className="kv__v" style={{ margin: 0 }}>
                          {count(result.document_one.fingerprint_count)} /{" "}
                          {count(result.document_two.fingerprint_count)}
                        </dd>
                        <dt className="kv__k">Shared</dt>
                        <dd className="kv__v" style={{ margin: 0 }}>
                          <strong className="accent">{count(result.shared_fingerprints)}</strong> hashes
                        </dd>
                        <dt className="kv__k">Config</dt>
                        <dd className="kv__v mono" style={{ margin: 0 }}>
                          k = {result.config.k} · t = {result.config.t} · {result.config.render_dpi} dpi
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>

                <div className="panel">
                  <div className="panel__head">
                    <span className="panel__title">
                      <GitCompareArrows size={13} aria-hidden="true" />
                      Both scores, side by side
                    </span>
                  </div>
                  <div className="panel__body">
                    <CompareBars
                      before={result.similarity_before}
                      after={result.similarity_after}
                    />
                  </div>
                </div>

                <div className="panel">
                  <div className="panel__head">
                    <span className="panel__title">
                      <ShieldAlert size={13} aria-hidden="true" />
                      Injection findings
                    </span>
                    <span
                      className={`badge ${result.injection_present ? "badge--danger" : "badge--accent"}`}
                    >
                      {result.injection_present ? "Injection present" : "Both clean"}
                    </span>
                  </div>
                  <div className="panel__body results__split">
                    <InjectionFindings doc={result.document_one} label={result.document_one.name} />
                    <InjectionFindings doc={result.document_two} label={result.document_two.name} />
                  </div>
                </div>

                {(result.document_one.projection || result.document_two.projection) && (
                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <Columns3 size={13} aria-hidden="true" />
                        Histogram projections · page 1
                      </span>
                      <span className="badge">HPH · VPH</span>
                    </div>
                    <div className="panel__body results__split">
                      {[result.document_one, result.document_two].map((doc, index) =>
                        doc.projection ? (
                          <div className="stack stack-3" key={`${doc.name}-${index}`}>
                            <span className="kv__k">{doc.name}</span>
                            <HistogramProjection
                              projection={doc.projection}
                              tone={doc.is_injected ? "danger" : "accent"}
                            />
                          </div>
                        ) : (
                          <div className="empty" key={`${doc.name}-${index}`}>
                            <span className="empty__glyph">
                              <FileText size={18} aria-hidden="true" />
                            </span>
                            <p className="meta">
                              {doc.name} has no page to read, because plain text carries no
                              layout.
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                <div className="results__split">
                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <Fingerprint size={13} aria-hidden="true" />
                        Fingerprint overlap
                      </span>
                    </div>
                    <div className="panel__body">
                      <FingerprintStrip
                        one={result.fingerprint_sample_one}
                        two={result.fingerprint_sample_two}
                        shared={result.shared_fingerprints}
                        totalOne={result.document_one.fingerprint_count}
                        totalTwo={result.document_two.fingerprint_count}
                      />
                    </div>
                  </div>

                  <div className="panel">
                    <div className="panel__head">
                      <span className="panel__title">
                        <Type size={13} aria-hidden="true" />
                        K-gram samples
                      </span>
                      <span className="badge">k = {result.config.k}</span>
                    </div>
                    <div className="panel__body stack stack-4">
                      <div className="stack stack-2">
                        <span className="kv__k">Document 1</span>
                        <div className="chip-set">
                          {result.kgram_sample_one.map((gram, index) => (
                            <span className="chip" key={`one-${index}`}>
                              {gram}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="stack stack-2">
                        <span className="kv__k">Document 2</span>
                        <div className="chip-set">
                          {result.kgram_sample_two.map((gram, index) => (
                            <span className="chip" key={`two-${index}`}>
                              {gram}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {!frozen && (
                <div className="panel">
                  <div className="panel__body row row--between row--wrap">
                    <div className="stack stack-2">
                      <span className="panel__title">
                        <FileDown size={13} aria-hidden="true" />
                        Full report
                      </span>
                      <p className="tile__body">
                        Both scores, everything that was hidden, and the text chunks behind the numbers.
                      </p>
                    </div>
                    <a
                      className="btn btn--primary"
                      href={reportUrl(result.report_id)}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <span className="btn__sheen" aria-hidden="true" />
                      <ExternalLink size={15} aria-hidden="true" />
                      Open PDF report
                      <span className="sr-only">(opens in a new tab)</span>
                    </a>
                  </div>
                </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <OfflineModal
        open={offlineOpen}
        onClose={() => setOfflineOpen(false)}
        onRunSample={async () => {
          const [clean, injected] = [findSample("clean"), findSample("injected")];
          const { fetchSampleFile } = await import("../lib/api");
          const files = await Promise.all([clean, injected].map((s) => fetchSampleFile(s)));
          loadSample(files);
        }}
      />
    </div>
  );
}

export default Compare;
